import { useState } from 'react'
import { CheckCircle2, Circle } from 'lucide-react'

/** OEM-specific Developer Options instructions keyed by model prefix */
const OEM_INSTRUCTIONS: Record<string, { name: string; path: string; notes?: string }> = {
  'SM-': { name: 'Samsung', path: 'Settings > About Phone > Software Information > Build Number (tap 7 times)', notes: 'Auto Blocker must be disabled first (Settings > Security > Auto Blocker)' },
  'Pixel': { name: 'Google Pixel', path: 'Settings > About Phone > Build Number (tap 7 times)' },
  'Redmi': { name: 'Xiaomi/Redmi', path: 'Settings > About Phone > MIUI Version (tap 7 times)', notes: 'Tap "MIUI Version", NOT "Build Number"' },
  'POCO': { name: 'Xiaomi/POCO', path: 'Settings > About Phone > MIUI Version (tap 7 times)', notes: 'Tap "MIUI Version", NOT "Build Number"' },
  'IN': { name: 'OnePlus', path: 'Settings > About Phone > Build Number (tap 7 times)', notes: 'USB mode must be set to "File Transfer"' },
  'moto': { name: 'Motorola', path: 'Settings > About Phone > Build Number (tap 7 times)' },
  'Nokia': { name: 'Nokia', path: 'Settings > About Phone > Build Number (tap 7 times)' },
  'Xperia': { name: 'Sony Xperia', path: 'Settings > About Phone > Build Number (tap 7 times)' },
  'HUAWEI': { name: 'Huawei', path: 'Settings > About Phone > Build Number (tap 7 times)', notes: 'Huawei uses "EMUI Version" or "Build Number" depending on model' },
}

function getOEMInstructions(model: string | null): { name: string; path: string; notes?: string } {
  if (!model) return { name: 'Android', path: 'Settings > About Phone > Build Number (tap 7 times)' }
  for (const [prefix, info] of Object.entries(OEM_INSTRUCTIONS)) {
    if (model.startsWith(prefix)) return info
  }
  return { name: 'Android', path: 'Settings > About Phone > Build Number (tap 7 times)' }
}

interface AndroidPrepChecklistProps {
  deviceModel: string | null
  onReady: () => void
  helperReady?: boolean
}

export default function AndroidPrepChecklist({
  deviceModel,
  onReady,
  helperReady = true,
}: AndroidPrepChecklistProps) {
  const [checks, setChecks] = useState({
    accountsRemoved: false,
    devOptionsEnabled: false,
    usbDebuggingEnabled: false,
  })

  const oemInfo = getOEMInstructions(deviceModel)
  const allChecked = checks.accountsRemoved && checks.devOptionsEnabled && checks.usbDebuggingEnabled

  const toggle = (key: keyof typeof checks) => {
    setChecks(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="space-y-5">
      <p className="text-gray-600 text-sm">
        Complete these steps on the Android phone before we begin setup.
      </p>

      <div className="space-y-3">
        <button onClick={() => toggle('accountsRemoved')} className="w-full text-left flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:border-brand transition-colors">
          {checks.accountsRemoved ? (
            <CheckCircle2 size={20} className="text-green-500 mt-0.5 shrink-0" />
          ) : (
            <Circle size={20} className="text-gray-300 mt-0.5 shrink-0" />
          )}
          <div>
            <p className="font-medium text-gray-900 text-sm">Remove all accounts from the phone</p>
            <p className="text-xs text-gray-500 mt-1">
              Go to Settings &gt; Accounts and remove every account (Google, Samsung, etc.). You can add them back in 2 minutes after setup.
            </p>
          </div>
        </button>

        <button onClick={() => toggle('devOptionsEnabled')} className="w-full text-left flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:border-brand transition-colors">
          {checks.devOptionsEnabled ? (
            <CheckCircle2 size={20} className="text-green-500 mt-0.5 shrink-0" />
          ) : (
            <Circle size={20} className="text-gray-300 mt-0.5 shrink-0" />
          )}
          <div>
            <p className="font-medium text-gray-900 text-sm">Enable Developer Options</p>
            <p className="text-xs text-gray-500 mt-1">{oemInfo.path}</p>
            {oemInfo.notes && (
              <p className="text-xs text-amber-600 mt-1">{oemInfo.notes}</p>
            )}
          </div>
        </button>

        <button onClick={() => toggle('usbDebuggingEnabled')} className="w-full text-left flex items-start gap-3 p-3 rounded-xl border border-gray-200 hover:border-brand transition-colors">
          {checks.usbDebuggingEnabled ? (
            <CheckCircle2 size={20} className="text-green-500 mt-0.5 shrink-0" />
          ) : (
            <Circle size={20} className="text-gray-300 mt-0.5 shrink-0" />
          )}
          <div>
            <p className="font-medium text-gray-900 text-sm">Enable USB Debugging</p>
            <p className="text-xs text-gray-500 mt-1">
              Settings &gt; Developer Options &gt; USB Debugging &gt; On
            </p>
          </div>
        </button>
      </div>

      {!helperReady && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
          <p className="text-amber-900 text-sm font-medium">Helper app disconnected</p>
          <p className="text-amber-800 text-xs mt-1">
            Re-open the Helper app and keep the Android phone plugged in before continuing.
          </p>
        </div>
      )}

      <button
        onClick={onReady}
        disabled={!allChecked || !helperReady}
        className="w-full py-3 bg-brand text-white rounded-xl hover:bg-brand-hover transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {helperReady ? 'Continue' : 'Open the Helper app to continue'}
      </button>
    </div>
  )
}
